import { Interfaces } from "@8x8/chalet-proto";
import { mergeMap } from "rxjs/operators";

import { actionCreators } from "../actions";
import { App } from "../app/App";
import { getIdleTs } from "../reducers/idle";
import { getFlag, getPeristent } from "../reducers/persist";
import { getNextRoomToClose, getOpenRoomIds, } from "../reducers/rooms";
import { getMonologUserId, getRoster, } from "../reducers/roster";
import { isFatalErrorWithCode } from "../utils/protoError";
import { Epic } from "../utils/types";

import { Data, RoomMeta } from "./Data";

export class ProtoProxy implements Interfaces.IProtoProxy {
    private _lastTypingTsPerRoom: {[roomId: string]: number} = {};
    private _didSync = false;
    private _wasOnline = false;

    constructor (private app: App) { }

    public epic: Epic = (action$, state$) => action$.pipe(
        mergeMap((action) => {
            if (action.type === actionCreators.JoinRoom.type) {
                const roomId = action.payload;
                if (this._didSync) {
                    this.joinRoom(roomId);
                }
            }
            else if (action.type === actionCreators.LeaveRoom.type) {
                const roomId = action.payload;
                this.app.proto.leaveRoom(roomId).then((data) => {
                    this.left(roomId, data);
                }, (error) => {
                    console.error("Failed to leave", roomId, error);
                });
            }
            else if (action.type === actionCreators.SetTyping.type) {
                this._typing(action.payload.room_id)
            }
            else if (action.type === actionCreators.onLogout.type) {
                this.disconnect();
            }
            else if (action.type === actionCreators.onSync.type) {
                this._didSync = true;
                this._wasOnline = true;

                const roomsToJoin = getOpenRoomIds(state$.value);
                roomsToJoin.map((roomId) => this.joinRoom(roomId));
                return [
                    actionCreators.getRoster()
                ];
            }
            return [];
        })
    )

    public disconnect() {
        this._didSync = false;
        this._wasOnline = false;
        this.app.actionCreators.setProtoToken({ token: undefined });
    }

    public destroy() {
        this.app.proto.setProxy(new Interfaces.DefaultProxy());
        this.app.actionCreators.setProtoToken({ token: undefined });
        this.app.actionCreators.onOffline({ goingToReconnect: false });
    }

    public onError(protoError: Interfaces.ProtoError) {
        const { type, error, info } = protoError;
        switch (type) {
            case "info_ping":
            case "info_ping_failed":
            case "info_ping_outdated_response": {
                // tslint:disable-next-line:no-console
                console.log(error, type, info)
                break;
            }
            case "info_set_session_token":
            case "info_update_session_token":
            case "info_wrong_session_token": {
                break;
            }
            default:  {
                console.error(error, type, info)
            }
        }
    }

    onWrongToken () {
        this.app.actionCreators.onWrongToken();
    }

    onRoomText(data: Data.RoomTextIn) {
        const state = this.app.rootStore.getState();
        const userId = getMonologUserId(state);
        const { id } = data.author_user;
        // new incoming message should be unseen, but messages from myself are seen
        data.unseen = (userId !== id);

        this.app.actionCreators.onRoomText({ data })
    }

    onUpdateRoomText(data: Data.UpdateRoomTextIn) {
        this.app.actionCreators.onUpdateRoomText({ data });
    }

    onDeleteRoomText(data: Data.DeleteRoomTextIn) {
        this.app.actionCreators.onDeleteRoomText({ data });
    }

    onSeenText (data: Data.SeenTextIn) {
        this.app.actionCreators.onSeenText(data);
    }

    onSeenAllText (data: Data.SeenAllTextIn) {
        this.app.actionCreators.onSeenAllText(data);
    }

    onUnseenText (data: Data.UnseenTextIn) {
        this.app.actionCreators.onUnseenText(data);
    }

    async onNotify (data : Data.NotifyIn) {
        const state = this.app.rootStore.getState();
        const userId = data.author_user && data.author_user.id;
        if (userId) {
            const dialog = getRoster(state).find(r => r.userId === userId);
            if (!dialog) {
                try {
                    const data = await this.app.proto.getRoster({ user_ids: [userId] }, { tag: "[notify author]" });
                    this.app.actionCreators.onRoster({ data });
                } catch (error) {
                    console.error("Failed to get author user for notification", error);
                }
            }
        }
        const roomId = data.room_id;
        if (roomId) {
            const hasRoomInRoster = getRoster(state).has(roomId);
            if (!hasRoomInRoster) {
                try {
                    const data = await this.app.proto.getRoster({ room_ids: [roomId] }, { tag: "[notify room]" });
                    this.app.actionCreators.onRoster({ data });
                } catch (error) {
                    console.error("Failed to get room for notification", error);
                }
            }
        }
        const ignore = !getIdleTs(state) || getFlag(state, "disable_notifications");
        this.app.actionCreators.onNotify({ data, ignore });
    }

    onTyping (data : Data.TypingIn)  {
        this.app.actionCreators.onTyping(data);
    }

    onRoster(data: Data.RoomsIn) {
        this.app.actionCreators.onRoster({ data, unsolicited: true, });
    }

    onReceipts(data: Data.ReceiptsIn) {
        this.app.actionCreators.onReceipts(data);
    }

    onRoomMeta(data: Data.MetaIn<RoomMeta>) {
        this.app.actionCreators.onRoomMeta(data);
    }

    private joined (roomId : string, data : Data.JoinIn) {
        const state = this.app.rootStore.getState();
        const { failedMessages } = getPeristent(state);
        this.app.actionCreators.onJoinedRoom({ roomId, data, failedMessages });
        this.app.proto.bundle(() => {
            this.app.proto.getReceipts(roomId, { tag: "[receipts]" }).then(
                data => {
                    this.onReceipts(data);
                },
                error => {
                    console.error("Failed to get receipts", error);
                }
            );
                this.app.proto.getRoomMeta(roomId, { tag: "[meta]" }).then(
                data => {
                    this.app.actionCreators.onRoomMeta(data);
                },
                error => {
                    console.error("Failed to get room meta", error);
                    if (isFatalErrorWithCode(error) && error.reason.code === "rate_limit") {
                        // https://app.clubhouse.io/chalet/story/743 to deal with too many rooms open
                        const room = getNextRoomToClose(this.app.rootStore.getState());
                        if (room) {
                            this.app.actionCreators.closeRoomRequested([room]);
                        }
                    }
                }
            );
            this.app.actionCreators.ensureRosterForRoom(roomId);
        });
    }

    private left (roomId : string, data : Data.LeaveIn) {
        this.app.actionCreators.onLeftRoom({ roomId, data });
    }

    private failedToJoin(roomId: string, error: any) {
        this.app.actionCreators.onJoinError({roomId, error})
    }

    onSync (data: Data.HelloIn) {
        this.app.actionCreators.onSync(data);
    }

    onOnline (data: Data.HelloIn) {
        this.app.actionCreators.onOnline(data);
    }

    onOffline () {
        this.app.actionCreators.onOffline({ goingToReconnect: this._wasOnline });
    }

    onDiscover(data: Data.Discover | undefined) {
        this.app.actionCreators.onDiscover(data)
    }

    onMutedRooms(_data: Data.MutedRoomsIn) {
        // TODO use new mute API
    }

    onUserStatus() {

    }

    onReaction(data: Data.ReactionIn) {
        this.app.actionCreators.onReactionUpdate({ data });
    }

    // Events below arrived in newer chalet-proto versions than this client
    // renders; the proxy must accept them but the app ignores them.
    onSummarize(_data: Data.SummarizeIn) {
    }

    onPinned(_data: Data.Pinned) {
    }

    onUnpinned(_data: Data.Unpinned) {
    }

    onPinnedTexts(_data: Data.PinnedTexts) {
    }

    onBookmarked(_data: Data.BookmarkedIn) {
    }

    onUnbookmarked(_data: Data.UnbookmarkedIn) {
    }

    onRoomGroupingUpdate(_data: Data.RoomGroupingIn) {
    }

    onRoomGroupingDelete(_data: Data.DeleteRoomGroupingIn) {
    }

    onAgentInteraction(_data: Data.AgentInteractionIn) {
    }

    onAgentInteractionList(_data: Data.AgentInteractionListIn) {
    }

    onAgentInteractionUpdate(_data: Data.AgentInteractionUpdateIn) {
    }

    onAgentState(_data: Data.AgentStateChangeIn) {
    }

    private _typing(roomId: string) {
        const now = new Date().getTime();
        const lastTs = this._lastTypingTsPerRoom[roomId] || 0;
        if (now > lastTs + 2000) {
            this.app.proto.setTyping(roomId).then((data) => {
                this.onTyping(data);
            }, (error) => {
                console.error("Failed to send typing", error);
            });
            this._lastTypingTsPerRoom[roomId] = now;
        }
    }

    joinRoom(roomId: string | { room_id: any }) {
        // Extract the actual string room_id, handling nested objects
        let actualRoomId: any = roomId;
        while (typeof actualRoomId === 'object' && actualRoomId !== null && 'room_id' in actualRoomId) {
            actualRoomId = actualRoomId.room_id;
        }

        if (typeof actualRoomId !== 'string') {
            console.error('[ProtoProxy] Invalid roomId:', roomId, 'extracted:', actualRoomId);
            return;
        }

        this.app.actionCreators.clearRoomError({ roomId: actualRoomId, errorId: undefined });
        this.app.proto.joinRoom({ room_id: actualRoomId, include_reactions: true }, {tag: "[join]"}).then((data) => {
            this.joined(actualRoomId, data)
        }, (error) => {
            this.failedToJoin(actualRoomId, error)
        });
        this.app.actionCreators.trackOpenRoomClickFinished({ roomId: actualRoomId });
    }
}
